import React from 'react';
import {
  AvatarCell,
  AvatarCellComponentProps,
  AvatarSkeleton,
  BadgeCell,
  BadgeCellComponentProps,
  ChipsCell,
  ChipsCellComponentProps,
  ChipsSkeleton,
  DropdownButtonCell,
  DropdownButtonCellComponentProps,
  DropdownButtonSkeleton,
  DropdownFilterOption,
  FilterItems,
  MenuItemProps,
} from '@odin-labs/components';
import {
  TempBadgeStatus,
  WorkerCardType,
} from 'apollo/generated/client-operations';
import { AuthContext } from 'auth';
import { to } from 'acl';
import {
  cn,
  ensureNonEmptyItems,
  getInitialsForUser,
  getUserFullName,
} from 'utils';
import { getPrettyFormattedDateTime } from 'utils/dates';
import {
  IdBadgeIcon,
  IdCardClipIcon,
  IdCardDuotoneIcon,
  MinusOctagonIcon,
  MobileIcon,
  PrintIcon,
  QrcodeIcon,
  RedoIcon,
  TurnDownLeftIcon,
} from 'components/icons';
import {
  TempBadge,
  JobsiteTempBadgeColumn,
  JobsiteTempBadgesFilters,
  TempBadgeModalValue,
} from './types';
import { useTempBadgeModal } from './modals/tempBadgeModal/components';

export enum TempBadgeColumnName {
  Name = 'name',
  CardNumber = 'cardNumber',
  Status = 'status',
  BadgeType = 'badgeType',
  AssignedTo = 'assignedTo',
  ExpiresOn = 'expiresOn',
  Actions = 'actions',
}

const tempBadgeStatusConfig: Record<
  TempBadgeStatus,
  Pick<BadgeCellComponentProps, 'text' | 'color'>
> = {
  // created is an internal status
  created: { text: 'Print Failed', color: 'red' },
  printing: { text: 'Printing...', color: 'blue' },
  printed: { text: 'Printed', color: 'purple' },
  printFailed: { text: 'Print Failed', color: 'red' },
  registered: { text: 'Registered', color: 'green' },
  assigned: { text: 'Assigned', color: 'yellow' },
  deactivated: { text: 'Deactivated', color: 'gray' },
};

const tempBadgeStatusOptions: DropdownFilterOption[] = [
  { value: TempBadgeStatus.Created, label: 'Created' },
  { value: TempBadgeStatus.Printing, label: 'Printing' },
  { value: TempBadgeStatus.Printed, label: 'Printed' },
  { value: TempBadgeStatus.PrintFailed, label: 'Print Failed' },
  { value: TempBadgeStatus.Registered, label: 'Registered' },
  { value: TempBadgeStatus.Assigned, label: 'Assigned' },
];

export const useColumns = ({
  jobsiteId,
  openModal,
}: {
  jobsiteId: string;
  openModal: (value: TempBadgeModalValue) => void;
}): JobsiteTempBadgeColumn[] => {
  const { currentUser: user } = React.useContext(AuthContext);
  const { openModal: openTempBadgeModal } = useTempBadgeModal();

  return React.useMemo<JobsiteTempBadgeColumn[]>(() => {
    const canManageTempBadges = user.isAllowed(to.manageTempBadges, jobsiteId);
    return ensureNonEmptyItems<JobsiteTempBadgeColumn>([
      {
        id: TempBadgeColumnName.Name,
        Header: 'Name',
        accessor: (tb: TempBadge): string => tb.name,
        disableSortBy: true,
      },
      {
        id: TempBadgeColumnName.CardNumber,
        Header: 'Badge Number',
        accessor: 'cardNumber',
        disableSortBy: true,
      },
      {
        id: TempBadgeColumnName.Status,
        Header: 'Status',
        Cell: BadgeCell,
        componentProps: (tb: TempBadge): BadgeCellComponentProps => ({
          text: tempBadgeStatusConfig[tb.status].text,
          color: tempBadgeStatusConfig[tb.status].color,
          className: cn('odin-uppercase odin-leading-relaxed odin-font-bold'),
        }),
        disableSortBy: true,
      },
      {
        id: TempBadgeColumnName.BadgeType,
        Header: 'Badge Type',
        accessor: (tb: TempBadge): string =>
          tb.jobsiteCardFormat?.workerCardFormat?.name,
        Cell: (props): React.ReactNode => {
          const { value } = props;
          return value && <ChipsCell {...props} />;
        },
        CellLoading: ChipsSkeleton,
        componentProps: (tb: TempBadge): ChipsCellComponentProps => {
          const { workerCardFormat } = tb?.jobsiteCardFormat ?? {};
          const { cardType, name, facilityCode } = workerCardFormat ?? {};
          return {
            chips: [
              {
                text: name,
                secondaryText: facilityCode?.toString(),
                withDot: false,
                icon:
                  // eslint-disable-next-line no-nested-ternary
                  cardType === WorkerCardType.QrCode
                    ? QrcodeIcon
                    : cardType === WorkerCardType.MobileBadge
                    ? MobileIcon
                    : IdBadgeIcon,
              },
            ],
          };
        },
        disableSortBy: true,
      },
      {
        id: TempBadgeColumnName.AssignedTo,
        Header: 'Assigned To',
        accessor: (tb: TempBadge): string => {
          const { user: awcUser } = tb?.activeWorkerCard?.worker ?? {};
          return getUserFullName(awcUser);
        },
        Cell: (props): React.ReactNode => {
          const { value } = props;
          return value && <AvatarCell {...props} />;
        },
        CellLoading: <AvatarSkeleton size="xs" hideDetails />,
        componentProps: (tb: TempBadge): AvatarCellComponentProps => ({
          src: tb.activeWorkerCard?.worker.profilePictureCropped?.downloadUrl,
          placeholder: getInitialsForUser(tb.activeWorkerCard?.worker.user),
          objectFit: 'cover',
          size: 'xs',
        }),
        disableSortBy: true,
      },
      {
        id: TempBadgeColumnName.ExpiresOn,
        Header: 'Expires On',
        accessor: (tb: TempBadge): string =>
          getPrettyFormattedDateTime(tb?.activeWorkerCard?.expiredAt),
        disableSortBy: true,
      },
      canManageTempBadges && {
        Header: '',
        id: 'actions',
        Cell: DropdownButtonCell,
        CellLoading: (): React.ReactElement => (
          <DropdownButtonSkeleton cellAlignment="right" />
        ),
        componentProps: (
          tempBadge: TempBadge,
        ): DropdownButtonCellComponentProps => ({
          cellAlignment: 'right',
          menuItems: ensureNonEmptyItems<MenuItemProps>([
            tempBadge.status === TempBadgeStatus.Printed && {
              icon: IdCardDuotoneIcon,
              text: 'Register',
              theme: 'secondary',
              onClick: (): void =>
                openTempBadgeModal({ tempBadge, modalAction: 'register' }),
            },
            (tempBadge.status === TempBadgeStatus.PrintFailed ||
              tempBadge.status === TempBadgeStatus.Created) && {
              icon: PrintIcon,
              text: 'Reprint',
              theme: 'secondary',
              onClick: (): void =>
                openTempBadgeModal({ tempBadge, modalAction: 'reprint' }),
            },
            tempBadge.status === TempBadgeStatus.Registered && {
              icon: IdCardClipIcon,
              text: 'Assign',
              theme: 'secondary',
              onClick: (): void =>
                openTempBadgeModal({ tempBadge, modalAction: 'assign' }),
            },
            tempBadge.status === TempBadgeStatus.Assigned && {
              icon: TurnDownLeftIcon,
              text: 'Return',
              theme: 'secondary',
              onClick: (): void =>
                openModal({ tempBadge, modalAction: 'return' }),
            },
            tempBadge.status !== TempBadgeStatus.Deactivated && {
              icon: MinusOctagonIcon,
              text: 'Deactivate',
              theme: 'danger',
              onClick: (): void =>
                openModal({ tempBadge, modalAction: 'deactivate' }),
            },
            tempBadge.status === TempBadgeStatus.Deactivated && {
              icon: RedoIcon,
              text: 'Reactivate',
              theme: 'secondary',
              onClick: (): void =>
                openTempBadgeModal({ tempBadge, modalAction: 'reactivate' }),
            },
          ]),
        }),
      },
    ]);
  }, [jobsiteId, user, openModal, openTempBadgeModal]);
};

export const getFilterItems = ({
  search,
  tempBadgeStatuses,
  showDeactivatedBadges,
}: {
  search: string;
  tempBadgeStatuses: string[];
  showDeactivatedBadges: boolean;
}): FilterItems<JobsiteTempBadgesFilters> => ({
  tempBadgeStatuses: {
    header: 'Status',
    type: 'dropdown',
    defaultValue: tempBadgeStatuses,
    componentProps: {
      options: tempBadgeStatusOptions,
      isMulti: true,
      allowSearch: false,
    },
  },
  showDeactivatedBadges: {
    header: 'Show Deactivated',
    type: 'checkbox',
    defaultValue: showDeactivatedBadges,
  },
  search: {
    header: '',
    type: 'searchInput',
    defaultValue: search,
    isVisibleOutsideFilterOnMobile: true,
  },
});
